import type { StatisticsOverviewData } from "../_types/statistics-api.types";
import type { DeltaVariant } from "./statisticsFormat.util";
import {
  formatPercentDelta,
  formatStatInt,
} from "./statisticsFormat.util";

export type StatisticsReportMetric = {
  heading: string;
  value: string;
  deltaLabel: string;
  variant: DeltaVariant;
};

export type StatisticsReportModel = {
  periodLabel: string;
  fromIso: string;
  toIso: string;
  metrics: [
    StatisticsReportMetric,
    StatisticsReportMetric,
    StatisticsReportMetric,
    StatisticsReportMetric,
  ];
};

function formatPeriodLabel(fromIso: string, toIso: string): string {
  const from = new Date(fromIso);
  const to = new Date(toIso);
  const fmt = new Intl.DateTimeFormat(undefined, { dateStyle: "medium" });
  return `${fmt.format(from)} – ${fmt.format(to)}`;
}

/**
 * Same numbers and deltas as the statistics bar tiles; null when data is not ready.
 */
export function buildStatisticsReportModel(
  overview: StatisticsOverviewData | null,
  prevOverview: StatisticsOverviewData | null,
  loading: boolean,
): StatisticsReportModel | null {
  if (loading || !overview) return null;

  const cur = overview.range;
  const prev = prevOverview?.range;
  const dash = "—";

  const activeDelta =
    cur && prev
      ? formatPercentDelta(
          cur.activeUsersInRange,
          prev.activeUsersInRange,
        )
      : null;
  const totalDelta =
    cur && prev
      ? formatPercentDelta(cur.totalUsersAtEnd, prev.totalUsersAtEnd)
      : null;
  const membersDelta =
    cur && prev
      ? formatPercentDelta(cur.memberUsersAtEnd, prev.memberUsersAtEnd)
      : null;
  const messagesDelta =
    cur && prev
      ? formatPercentDelta(
          cur.formsSubmittedInRange,
          prev.formsSubmittedInRange,
        )
      : null;

  const metrics: StatisticsReportModel["metrics"] = [
    {
      heading: "Active users",
      value: formatStatInt(cur.activeUsersInRange),
      deltaLabel: activeDelta?.label ?? dash,
      variant: activeDelta?.variant ?? "none",
    },
    {
      heading: "Total accounts",
      value: formatStatInt(cur.totalUsersAtEnd),
      deltaLabel: totalDelta?.label ?? dash,
      variant: totalDelta?.variant ?? "none",
    },
    {
      heading: "Members",
      value: formatStatInt(cur.memberUsersAtEnd),
      deltaLabel: membersDelta?.label ?? dash,
      variant: membersDelta?.variant ?? "none",
    },
    {
      heading: "Messages",
      value: formatStatInt(cur.formsSubmittedInRange),
      deltaLabel: messagesDelta?.label ?? dash,
      variant: messagesDelta?.variant ?? "none",
    },
  ];

  return {
    periodLabel: formatPeriodLabel(overview.from, overview.to),
    fromIso: overview.from,
    toIso: overview.to,
    metrics,
  };
}
